<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useRouter } from 'vue-router';
import wootConstants from 'dashboard/constants/globals';

import TicketListPanel from './TicketListPanel.vue';
import TicketDetails from './TicketDetails.vue';
import CreateTicketModal from './CreateTicketModal.vue';

const props = defineProps({
  ticketType: {
    type: String,
    default: 'all',
  },
  ticketId: {
    type: [String, Number],
    default: 0,
  },
});

const store = useStore();
const router = useRouter();
const { uiSettings } = useUISettings();

const isOnExpandedLayout = computed(() => {
  const {
    LAYOUT_TYPES: { CONDENSED },
  } = wootConstants;
  const { conversation_display_type: displayType = CONDENSED } =
    uiSettings.value;
  return displayType !== CONDENSED;
});

const showTicketList = computed(() => {
  return isOnExpandedLayout.value ? !props.ticketId : true;
});

const showTicketDetail = computed(() => {
  return props.ticketId ? true : !isOnExpandedLayout.value;
});

const onTicketSelect = ticketDisplayId => {
  const routeNameMap = {
    all: 'ticket_details',
    mention: 'ticket_through_mentions',
    unattended: 'ticket_through_unattended',
  };
  const routeName = routeNameMap[props.ticketType] || 'ticket_details';
  router.push({
    name: routeName,
    params: { ticketId: ticketDisplayId },
  });
};

const showCreateModal = ref(false);
const ticketListPanelRef = ref(null);

const onCreateTicket = () => {
  showCreateModal.value = true;
};

const onTicketCreated = () => {
  showCreateModal.value = false;
  ticketListPanelRef.value?.fetchTickets();
};

onMounted(() => {
  store.dispatch('agents/get');
});
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <TicketListPanel
      v-if="showTicketList"
      ref="ticketListPanelRef"
      :ticket-type="ticketType"
      :active-ticket-id="ticketId"
      @ticket-select="onTicketSelect"
      @create="onCreateTicket"
    />
    <TicketDetails
      v-if="showTicketDetail && ticketId"
      :ticket-id="ticketId"
      embedded
    />
    <div
      v-else-if="showTicketDetail && !ticketId"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      {{ $t('TICKETS.SELECT_TICKET') }}
    </div>
    <CreateTicketModal
      v-if="showCreateModal"
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="onTicketCreated"
    />
  </section>
</template>
